export interface ActionableGoogleMimeBehavior {
  action: 'direct' | 'export';
  outMime: string;
  extension: string;
}

export type GoogleMimeTypeBehavior = {action: 'not_allowed'} | ActionableGoogleMimeBehavior;

export enum GoogleDriveIssueType {
  GoogleDriveUnreachable,
  UnableToExportFile,
  UnableToDirectDownloadFile,
  InvalidFilePicked,
}

export interface GoogleDriveIssue {
  issueType: GoogleDriveIssueType;
  userDescription: string;
  devDescription: string;
  googleErrorMessage?: string;
}

async function getGoogleErrorMessageFromResponse(response: Response): Promise<string> {
  const responseJson = await response.json();
  return `${JSON.stringify(responseJson?.error?.message ?? 'Unknown error')}`;
}

function getPrefixUserDescriptionForDownloadOrExport(response: Response): string {
  if (response.status >= 500 && response.status < 600) {
    return 'Google had an error processing your request to ';
  } else if (response.status > 400 && response.status < 500) {
    return 'Google rejected your request to ';
  } else {
    return 'There was an issue processing your request to ';
  }
}

export async function createUnableToDirectDownloadFileIssue(
  inputFileInfo: InputFileInfo,
  response: Response
): Promise<GoogleDriveIssue> {
  let suffixUserDescription: string = `download file "${inputFileInfo.inputFileName}"`;
  const googleErrorMessage = await getGoogleErrorMessageFromResponse(response);
  if (response.status > 400 && response.status < 500) {
    suffixUserDescription += `: ${googleErrorMessage}`;
  }

  return {
    issueType: GoogleDriveIssueType.UnableToDirectDownloadFile,
    userDescription: getPrefixUserDescriptionForDownloadOrExport(response) + suffixUserDescription,
    devDescription: `HTTP ${response.status}: ${response.statusText} error in downloading file "${inputFileInfo.inputFileName}"`,
    googleErrorMessage,
  };
}

export async function createUnableToExportFileIssue(
  inputFileInfo: InputFileInfo,
  outputFileInfo: ExportOutputFileInfo,
  response: Response
): Promise<GoogleDriveIssue> {
  let suffixUserDescription: string = `export file "${inputFileInfo.inputFileName}" to file type ${outputFileInfo.outputFileExtension}`;
  const googleErrorMessage = await getGoogleErrorMessageFromResponse(response);
  if (response.status > 400 && response.status < 500) {
    suffixUserDescription += `: ${googleErrorMessage}`;
  }

  return {
    issueType: GoogleDriveIssueType.UnableToExportFile,
    userDescription: getPrefixUserDescriptionForDownloadOrExport(response) + suffixUserDescription,
    devDescription: `HTTP ${response.status}: ${response.statusText} error in exporting file "${inputFileInfo.inputFileName}" with mimeType "${inputFileInfo.inputFileMimeType}" to mimeType "${outputFileInfo.outputFileMimeType}"`,
    googleErrorMessage,
  };
}

export const GoogleDriveUnreachableIssue: GoogleDriveIssue = {
  issueType: GoogleDriveIssueType.GoogleDriveUnreachable,
  userDescription: 'Cannot reach Google Drive',
  devDescription: 'Cannot reach Google Drive',
};

export interface InputFileInfo {
  inputFileId: string;
  inputFileName: string;
  inputFileMimeType: string;
}

export interface DirectDownloadOutputFileInfo {
  outputFileName: string;
  outputFileMimeType: string;
}

export interface ExportOutputFileInfo extends DirectDownloadOutputFileInfo {
  outputFileExtension: string;
}

export type DownloadSingleGoogleDriveFileInstructions =
  | {
      action: 'direct';
      inputFileInfo: InputFileInfo;
      outputFileInfo: DirectDownloadOutputFileInfo;
    }
  | {
      action: 'export';
      inputFileInfo: InputFileInfo;
      outputFileInfo: ExportOutputFileInfo;
    };

export type DownloadResult =
  | {fileId: string; file: File; error: null}
  | {fileId: string; error: GoogleDriveIssue; file: null};

export function createInvalidFilePickedDownloadResult(
  fileId: string,
  userDescription: string,
  devDescription: string
): DownloadResult {
  return {
    fileId,
    file: null,
    error: {
      issueType: GoogleDriveIssueType.InvalidFilePicked,
      userDescription,
      devDescription,
    },
  };
}
